import {
  sortingQueryParams,
  toSortingQueryParams,
} from '@lmt/unicorn-shared/app/components/common/table/operations';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { BooleanParam, withDefault } from 'react-router-url-params';
import { Button, ButtonColor } from '../common/ui-kit/buttons/Button';
import { TableTitle } from '../common/table/TableTitle';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import styles from '@lmt/unicorn-shared/app/components/patients/list/PatientListPage.module.scss';
import localStyles from './MergeAssistant.module.scss';

import { ReactComponent as AddIcon } from '@lmt/unicorn-shared/app/icons/plus-blue.svg';
import { useGlobalForms } from '../global-forms/useGlobalForms';
import { Loading } from '../common/Loading';
import { PatientDuplicateTable } from './list/PatientDuplicateTable';
import { PatientDuplicatePairDto, QueryFactory } from '@lmt/shared-types';

export const MergeAssistant: React.FC = React.memo(function MergeAssistant() {
  const i18next = useScopedTranslation('merge_assistant');
  const i18nextGlobal = useTranslation();
  const forms = useGlobalForms();
  const queryClient = useQueryClient();
  const [queryParams, setQueryParams] = useQueryParamsInTab({
    showDeleted: withDefault(BooleanParam, false),
    ...sortingQueryParams(
      nameof<PatientDuplicatePairDto>('lastActivity'),
      true,
    ),
  });
  const [selectedPairs, setSelectedPairs] = useState<number[]>([]);
  const [isSelectEnabled, setSelectEnabled] = useState<boolean>(false);

  const duplicatesQuery = QueryFactory.PatientDuplicateQuery.useGetQuery({
    showDeleted: queryParams.showDeleted,
    ...toSortingQueryParams(queryParams),
  });

  return (
    <div className={styles.tableContainer} data-test-id="table-page-container">
      <div className={localStyles.header}>
        <TableTitle
          title={i18next.t('title')}
          count={duplicatesQuery.data?.totalCount ?? 0}
        />
        <div className={styles.toolbar}>
          {selectedPairs.length > 0 && (
            <Button
              title={
                queryParams.showDeleted
                  ? i18next.t('restore_selected')
                  : i18next.t('skip_selected')
              }
              className={localStyles.skipAllButton}
              onClick={async () => {
                const showDeleted = queryParams.showDeleted;
                for (const id of selectedPairs) {
                  if (showDeleted) {
                    await QueryFactory.PatientDuplicateClient.patch(id, {
                      isDeleted: false,
                    });
                  } else {
                    await QueryFactory.PatientDuplicateClient.remove(id);
                  }
                }
                setSelectedPairs([]);
                await queryClient.invalidateQueries({
                  queryKey: QueryFactory.PatientDuplicateQuery.getQueryKey(
                    queryParams.showDeleted,
                  ),
                });
              }}
            />
          )}
          <CheckBox
            checked={queryParams.showDeleted}
            onChange={(event) => {
              setSelectedPairs([]);
              setSelectEnabled(false);
              const showDeleted = event.currentTarget.checked;
              setQueryParams({
                showDeleted: showDeleted,
              });
              void queryClient.invalidateQueries({
                queryKey: QueryFactory.PatientDuplicateQuery.getQueryKey({
                  showDeleted: showDeleted,
                }),
              });
            }}
            title={i18next.t('show_skipped')}
            name={'showSkipped'}
            testId="show-skipped-pairs"
          />
          <Button
            className={localStyles.addButton}
            iconComponent={<AddIcon />}
            title={i18next.t('add_button')}
            color={ButtonColor.Primary}
            onClick={() => forms.openMergePair()}
          />
        </div>
      </div>
      <div className={localStyles.page}>
        <Loading
          loading={duplicatesQuery.isLoading}
          title={i18nextGlobal.t('load.loading')}
          className={localStyles.loading}
        >
          <PatientDuplicateTable
            data={duplicatesQuery.data?.data ?? []}
            isNoDuplicatesFound={duplicatesQuery.data?.totalCount === 0}
            selectedPairs={selectedPairs}
            setSelectedPairs={setSelectedPairs}
            setSorting={setQueryParams}
            isSelectEnabled={isSelectEnabled}
            setSelectEnabled={setSelectEnabled}
          />
        </Loading>
      </div>
    </div>
  );
});
